import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchLastRefreshTime } from "@/features/admin/store/configSlice";
import { useTimezone } from "@/contexts/TimeZoneContext";
import type { RefreshTimePayload } from "@/shared/types/common.types";
import { selectIsAdmin, selectAllowedTabs } from "@/store/authSlice";

function SubNav() {
  const dispatch = useAppDispatch();
  const LastRefreshTime = useAppSelector(
    (state) => state.config._lastRefreshedTimeDst,
  );

  const { timezone } = useTimezone();
  const location = useLocation();

  const navigate = useNavigate();
  const isAdmin = useAppSelector(selectIsAdmin);
  const allowedTabs = useAppSelector(selectAllowedTabs);
  const [currentPage, setCurrentPage] = useState("CriticalAlerts");
  const [lstRfrshVisible, setLstRfrshVisible] = useState(true);

  // All possible tabs — filtered based on role or existing isAdmin logic
  const allTabs = useMemo(
    () => [
      { id: 1, label: "Alerts", route: "/", page: "CriticalAlerts", code: "ALERTS" },
      { id: 2, label: "Security Alerts", route: "/securityAlerts", page: "SecurityAlerts", code: "SECURITY_ALERTS" },
      { id: 3, label: "Deployments", route: "/deployments", code: "DEPLOYMENT" },
      { id: 9, label: "Service Health", route: "/serviceHealth", code: "SERVICE_HEALTH" },
      { id: 5, label: "Alerts Trend", route: "/alertsTrend", code: "ALERTS_TREND" },
      // Cost Management is RBAC-assignable: visible to admins, or to any role granted the COST tab.
      ...( (isAdmin || (allowedTabs?.includes("COST") ?? false))
        ? [{ id: 7, label: "Cost Management", route: "/costManagement/dashboard", code: "COST" }]
        : []),
      // Administration: the admin-only RBAC management console (users, roles, tabs, audit).
      ...(isAdmin ? [{ id: 8, label: "Administration", route: "/admin", code: "ADMIN" }] : []),
    ],
    [isAdmin, allowedTabs],
  );

  // If the user has RBAC tab permissions, filter tabs by them. Otherwise fall back to existing logic.
  const tabs = useMemo(() => {
    let list = allTabs;

    if (allowedTabs !== null) {
      const hasServiceHealth =
        allowedTabs.includes("SERVICE_HEALTH") ||
        allowedTabs.includes("SERVICEHEALTH");

      list = allTabs.filter((t) => {
        if (t.code === "SERVICE_HEALTH") {
          return hasServiceHealth;
        }
        return allowedTabs.includes(t.code);
      });
    }

    // Administration governs RBAC itself, so an admin must always be able to reach it even when no
    // explicit ADMIN tab-permission row exists yet (avoids locking admins out of the console).
    if (isAdmin && !list.some((t) => t.code === "ADMIN")) {
      const adminTab = allTabs.find((t) => t.code === "ADMIN");
      if (adminTab) list = [...list, adminTab];
    }

    // Keep Service Health discoverable for admins if RBAC payload is temporarily stale.
    if (isAdmin && !list.some((t) => t.code === "SERVICE_HEALTH")) {
      const serviceHealthTab = allTabs.find((t) => t.code === "SERVICE_HEALTH");
      if (serviceHealthTab) list = [...list, serviceHealthTab];
    }

    // Preserve canonical tab order from allTabs after any admin fallback insertions.
    const visibleCodes = new Set(list.map((t) => t.code));
    list = allTabs.filter((t) => visibleCodes.has(t.code));

    return list;
  }, [allTabs, allowedTabs, isAdmin]);

  const getActivePage = () => {
    const path = location.pathname;

    if (path === "/securityAlerts") return 2;
    else if (path === "/deployments") return 3;
    else if (path === "/serviceHealth") return 9;
    else if (path === "/alertsTrend") return 5;
    else if (path === "/virtualMachines") return 6;
    else if (path.startsWith("/costManagement")) return 7;
    else if (path.startsWith("/admin")) return 8;
    else return 1;
  };

  const activePage = getActivePage();

  // --- Decide if Last Refresh should be visible ---
  useEffect(() => {
    const routesWithRefresh = ["/", "/securityAlerts"];
    setLstRfrshVisible(routesWithRefresh.includes(location.pathname));
  }, [location.pathname]);


  useEffect(() => {
    if (!timezone) return;

    const payload: RefreshTimePayload = {
      currentPage,
      timezone: timezone?.ianaTimeZone!,
    };
    dispatch(fetchLastRefreshTime(payload));

    const interval = setInterval(() => {
      dispatch(fetchLastRefreshTime(payload));
    }, 10000);

    return () => clearInterval(interval);
  }, [timezone, currentPage, dispatch]);

  // --- Handle tab click ---
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    if (tab.page) setCurrentPage(tab.page);
    navigate(tab.route);
  };

  // --- Utility: format refresh time ---
  const formatReportedAt = (date?: Date | string | null) =>
    date ? dayjs(date).format("MM/DD/YYYY hh:mm:ss A") : "--";

  const getClass = (pageNumber: number) =>
    `cursor-pointer rounded-t h-full flex items-center justify-center px-2 sm:px-3 md:px-4 lg:px-5 text-[11px] sm:text-[12px] md:text-[12.5px] font-medium transition-colors flex-shrink-0 ${
      activePage === pageNumber
        ? "text-[var(--nav-text-active)] border-b-2 border-[var(--nav-active-indicator)]"
        : "text-[var(--nav-text-default)] hover:bg-[var(--nav-hover-bg)] hover:text-[var(--nav-text-active)]"
    }`;

  return (
    <div className="flex flex-row bg-[var(--nav-secondary-bg)] items-center justify-between text-[var(--nav-text-default)] font-segoe border-b border-[var(--border-default)] h-9 overflow-hidden">
      <nav
        className="flex flex-row items-center flex-1 min-w-0 overflow-x-auto h-full pl-2 sm:pl-4 md:pl-8 lg:pl-12"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={getClass(tab.id)}
            onClick={() => handleTabClick(tab)}
          >
            <p className="mt-1 whitespace-nowrap">{tab.label}</p>
          </div>
        ))}
      </nav>

      {/* Last Refresh — hidden on small screens */}
      {lstRfrshVisible && (
        <div className="hidden md:flex flex-row gap-1.5 items-center text-[12.5px] pr-6 text-[var(--text-primary)] flex-shrink-0 pl-4">
          <i className="fa-solid fa-arrows-rotate" style={{ color: "var(--text-primary)" }} />
          <p className="m-0 whitespace-nowrap">Last Refresh: {formatReportedAt(LastRefreshTime)}</p>
        </div>
      )}
    </div>
  );
}

export default SubNav;
